// Lightweight, key-free routing helpers using OpenStreetMap services.
// Nominatim for geocoding (address -> lat/lng) and OSRM for driving ETA.
// Results are cached in-memory to respect fair-use rate limits.

const geoCache = new Map();

export async function geocode(address) {
  const q = (address || "").trim();
  if (!q) return null;
  const key = q.toLowerCase();
  if (geoCache.has(key)) return geoCache.get(key);
  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`;
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    const data = await res.json();
    const hit = Array.isArray(data) && data[0] ? { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) } : null;
    geoCache.set(key, hit);
    return hit;
  } catch {
    geoCache.set(key, null);
    return null;
  }
}

export async function routeEta(from, to) {
  if (!from || !to || from.lat == null || to.lat == null) return null;
  try {
    const url = `https://router.project-osrm.org/route/v1/driving/${from.lng},${from.lat};${to.lng},${to.lat}?overview=false`;
    const res = await fetch(url);
    const data = await res.json();
    const rt = data && data.routes && data.routes[0];
    if (!rt) return null;
    return { durationMin: Math.max(1, Math.round(rt.duration / 60)), distanceMi: +(rt.distance / 1609.34).toFixed(1) };
  } catch {
    return null;
  }
}

// Which address is the driver currently heading to, based on status.
export function destForCall(c) {
  const beforeScene = ["dispatched", "accepted"].includes(c.status);
  const address = beforeScene
    ? [c.pickup_location, c.pickup_city_state].filter(Boolean).join(", ")
    : [c.dropoff_location, c.dropoff_city_state].filter(Boolean).join(", ");
  return { address, label: beforeScene ? "pickup" : "drop-off" };
}

// Google Maps turn-by-turn link. Origin is the driver's live coords when known.
export function mapsFromTo(from, destAddress) {
  const origin = from && from.lat != null ? `&origin=${from.lat},${from.lng}` : "";
  return `https://www.google.com/maps/dir/?api=1${origin}&destination=${encodeURIComponent(destAddress || "")}&travelmode=driving`;
}

// Most recent known GPS point from a call's tracking object.
export function lastGpsFromTracking(tracking) {
  if (!tracking) return null;
  const stages = ["completed", "delivered", "transporting", "on_scene", "accepted", "dispatched"];
  for (const s of stages) {
    const lat = tracking[`${s}_lat`];
    const lng = tracking[`${s}_lng`];
    if (lat != null && lng != null) return { lat, lng };
  }
  return null;
}
